import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import CreatableSelect from 'react-select/creatable'
import { useFormikContext } from 'formik'
import makeAnimated from 'react-select/animated'
import PropTypes from 'prop-types'
import { returnSelectionFieldStyle } from '../../constants/common'
import { string } from 'yup'
import ExperienceFieldLabel from '../Quests/ExperienceFieldLabel'

const animatedComponents = makeAnimated()

const emailValidationSchema = string()
  .email('Enter a valid email address (e.g., name@example.com).')
  .matches(
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    'Enter a valid email address (e.g., name@example.com).',
  )
  .max(255)
  .required('Enter a valid email address')

/**
 * New-experience email multi-select (v7 Build accordion styling).
 * Mirrors EmailMultiSelectField props / validation — leave the shared
 * EmailMultiSelectField alone until a global swap is approved.
 */
const ExperienceEmailMultiSelectField = ({
  label,
  name,
  placeholder = 'Enter email addresses',
  disabled = false,
  className = '',
  maxMenuHeight = null,
  onValidationChange,
  resetKey = 0,
  onBlur: onBlurProp,
  where = [],
  hint,
}) => {
  const {
    values,
    touched,
    errors,
    handleBlur,
    setFieldTouched,
    setFieldValue,
  } = useFormikContext()

  const [inputValue, setInputValue] = useState('')
  const [emailError, setEmailError] = useState('')

  const baseStyles = returnSelectionFieldStyle(disabled, [])
  const isFieldInvalid =
    (touched.hasOwnProperty(name) && errors.hasOwnProperty(name)) ||
    emailError !== ''
  const styles = {
    ...baseStyles,
    control: (provided, state) => ({
      ...provided,
      ...baseStyles.control(provided, state),
      border: `1px solid ${
        isFieldInvalid
          ? 'var(--stop, #c73939)'
          : state.isFocused
          ? 'var(--gold, #dfae3f)'
          : 'var(--line2, #cfccc4)'
      }`,
      borderRadius: 7,
      minHeight: 38,
      boxShadow: state.isFocused
        ? isFieldInvalid
          ? '0 0 0 2px rgba(199, 57, 57, 0.2)'
          : '0 0 0 2px rgba(223, 174, 63, 0.25)'
        : 'none',
      fontSize: 13.5,
      fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
      background: 'var(--paper, #fff)',
    }),
    valueContainer: (provided, state) => ({
      ...baseStyles.valueContainer(provided, state),
      border: 0,
      backgroundColor: 'transparent',
      borderRadius: 7,
      fontSize: 13.5,
    }),
    input: (provided) => ({
      ...baseStyles.input(provided),
      fontSize: 13.5,
      fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: 12.5,
      fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    }),
  }

  useEffect(() => {
    setEmailError('')
    setInputValue('')
  }, [resetKey])

  useEffect(() => {
    if (typeof onValidationChange === 'function') {
      onValidationChange(emailError !== '')
    }
  }, [emailError, onValidationChange])

  const validateEmail = (email) => {
    try {
      emailValidationSchema.validateSync(email)
      return true
    } catch (err) {
      return false
    }
  }

  const validateEmailWithError = async (email) => {
    try {
      await emailValidationSchema.validate(email)
      setEmailError('')
      return true
    } catch (err) {
      setEmailError(err.message)
      return false
    }
  }

  const handleInputChange = (newValue) => {
    setInputValue(newValue)
    if (newValue && newValue.trim() !== '') {
      validateEmailWithError(newValue.trim())
    } else {
      setEmailError('')
    }
  }

  const addEmail = (email) => {
    const currentValues = values[name] || []
    const emailExists = currentValues.some(
      (item) => item.value.toLowerCase() === email.toLowerCase(),
    )
    if (emailExists) {
      setEmailError('This email is already added')
      return
    }
    setFieldValue(name, [...currentValues, { value: email, label: email }])
    setInputValue('')
    setEmailError('')
  }

  const handleKeyDown = (event) => {
    if (!inputValue) return
    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case ',': {
        event.preventDefault()
        const email = inputValue.trim()
        if (!email) return
        if (validateEmail(email)) {
          addEmail(email)
        } else {
          validateEmailWithError(email)
        }
        break
      }
      default:
        break
    }
  }

  const handleChange = (selectedOptions) => {
    setFieldValue(name, selectedOptions || [])
    setEmailError('')
  }

  const handleCreateOption = (value) => {
    const email = value.trim()
    if (email && validateEmail(email)) {
      addEmail(email)
    } else if (email) {
      validateEmailWithError(email)
    }
  }

  const isValidNewOption = (val) => {
    const email = (val || '').trim()
    if (!email) return false
    return validateEmail(email)
  }

  const labelContent =
    label && where.length > 0 ? (
      <ExperienceFieldLabel where={where}>{label}</ExperienceFieldLabel>
    ) : (
      label
    )

  return (
    <div className={`experience-f form-group mb-0 ${className}`}>
      {label ? (
        <Form.Label className='experience-f-label'>{labelContent}</Form.Label>
      ) : null}
      <CreatableSelect
        value={values[name] || []}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCreateOption={handleCreateOption}
        closeMenuOnSelect={false}
        components={animatedComponents}
        isMulti
        menuIsOpen={false}
        isValid={touched.hasOwnProperty(name) && !errors.hasOwnProperty(name)}
        isInvalid={
          (touched.hasOwnProperty(name) && errors.hasOwnProperty(name)) ||
          emailError !== ''
        }
        placeholder={placeholder}
        styles={styles}
        onFocus={() => {
          setFieldTouched(name, true, false)
        }}
        onBlur={(e) => {
          handleBlur(e)
          if (inputValue && inputValue.trim() !== '') {
            const email = inputValue.trim()
            if (validateEmail(email)) {
              addEmail(email)
            } else {
              validateEmailWithError(email)
            }
          }
          if (typeof onBlurProp === 'function') {
            onBlurProp(e)
          }
        }}
        isValidNewOption={isValidNewOption}
        isClearable
        isDisabled={disabled}
        formatCreateLabel={(val) => `Add "${val}"`}
        noOptionsMessage={() => null}
        {...(maxMenuHeight && { maxMenuHeight })}
      />
      {hint ? <p className='experience-f-hint'>{hint}</p> : null}
      {emailError ? (
        <small className='experience-f-error'>
          <span className='text-error d-flex justify-content-end'>
            {emailError}
          </span>
        </small>
      ) : null}
      {touched[name] && errors[name] && !emailError ? (
        <small className='experience-f-error'>
          <span className='text-error d-flex justify-content-end'>
            {errors[name]}
          </span>
        </small>
      ) : null}
    </div>
  )
}

ExperienceEmailMultiSelectField.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  maxMenuHeight: PropTypes.number,
  onValidationChange: PropTypes.func,
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlur: PropTypes.func,
  where: PropTypes.arrayOf(PropTypes.string),
  hint: PropTypes.node,
}

export default ExperienceEmailMultiSelectField
